import { defaultStorage } from 'aws-amplify/utils';

/**
 * The raw Cognito tokens authWidget hands off via `#auth_data=` (and the
 * shape `widgetAuthentication()` repackages for the reverse direction).
 */
export interface RawCognitoTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  username: string;
}

/**
 * `cognitoUserPoolsTokenProvider`'s localStorage key prefix (v6). Confirmed
 * against the real installed `aws-amplify@6.18.0` source —
 * `@aws-amplify/auth/dist/esm/providers/cognito/tokenProvider/constants.mjs`
 * — unchanged from v5's `amazon-cognito-identity-js` format, but this is an
 * UNDOCUMENTED internal contract, not a public Amplify API. Re-validate
 * whenever `aws-amplify` is upgraded (see design.md "Open Questions").
 */
const AUTH_KEY_PREFIX = 'CognitoIdentityServiceProvider';

const getLastAuthUserKey = (clientId: string) =>
  `${AUTH_KEY_PREFIX}.${clientId}.LastAuthUser`;

const getUserTokenKey = (
  clientId: string,
  username: string,
  tokenName: 'idToken' | 'accessToken' | 'refreshToken' | 'clockDrift',
) => `${AUTH_KEY_PREFIX}.${clientId}.${username}.${tokenName}`;

/**
 * Seeds Amplify v6's `cognitoUserPoolsTokenProvider` localStorage keys from
 * authWidget's raw hand-off tokens, so a subsequent `fetchAuthSession()`
 * hydrates a session without the user re-entering credentials.
 *
 * Confirmed against `@aws-amplify/auth`'s `DefaultTokenStore.loadTokens()`:
 * `accessToken` is the only key that MUST exist for the session to hydrate;
 * `LastAuthUser` is also required in practice so the per-user key lookup
 * resolves to this seeded namespace instead of the store's `'username'`
 * literal fallback. `idToken`/`refreshToken`/`clockDrift` are optional but
 * written here for parity with a real Cognito-issued session — no
 * `signInDetails`/device keys are required.
 */
export const seedCognitoTokenStorage = async (
  tokens: RawCognitoTokens,
  clientId: string,
): Promise<void> => {
  const { idToken, accessToken, refreshToken, username } = tokens;

  await defaultStorage.setItem(getLastAuthUserKey(clientId), username);
  await defaultStorage.setItem(
    getUserTokenKey(clientId, username, 'idToken'),
    idToken,
  );
  await defaultStorage.setItem(
    getUserTokenKey(clientId, username, 'accessToken'),
    accessToken,
  );
  await defaultStorage.setItem(
    getUserTokenKey(clientId, username, 'refreshToken'),
    refreshToken,
  );
  await defaultStorage.setItem(
    getUserTokenKey(clientId, username, 'clockDrift'),
    '0',
  );
};

/**
 * Reads back the raw tokens Amplify v6 has stored for the current
 * `LastAuthUser`, in the same shape `seedCognitoTokenStorage` accepts.
 *
 * This is the companion "read" half of the same undocumented storage
 * contract, used by `widgetAuthentication()` (Root.tsx) to repackage the
 * *current* session's tokens for a NEW `#auth_data=` hand-off. v6's public
 * `fetchAuthSession()` API intentionally never exposes the raw refresh
 * token to application code (confirmed: `TokenOrchestrator.getTokens()`
 * returns only `{accessToken, idToken, signInDetails}`), so reading these
 * same storage keys back out is the only way to get it.
 */
export const readCognitoTokenStorage = async (
  clientId: string,
): Promise<RawCognitoTokens | null> => {
  const username = await defaultStorage.getItem(getLastAuthUserKey(clientId));

  if (!username) {
    return null;
  }

  const [idToken, accessToken, refreshToken] = await Promise.all([
    defaultStorage.getItem(getUserTokenKey(clientId, username, 'idToken')),
    defaultStorage.getItem(getUserTokenKey(clientId, username, 'accessToken')),
    defaultStorage.getItem(
      getUserTokenKey(clientId, username, 'refreshToken'),
    ),
  ]);

  if (!accessToken) {
    return null;
  }

  return { idToken, accessToken, refreshToken, username };
};
